/**
 * Enhanced InMocean File Validation Service
 * Smart validation: Full validation for small files, fast-track for large files
 * NOW SUPPORTS BOTH COSTING AND ORDER FILES
 */

import * as XLSX from 'xlsx';
import { INMOCEAN_FILE_SCHEMAS } from '../config/fileSchemas';

// Add order schemas to the combined schema object
const INMOCEAN_ORDER_SCHEMAS = {
  order_tracking: {
    name: 'Order Tracking Data',
    description: 'Comprehensive order tracking with customer details, pricing, and status information',
    icon: '📋',
    tableName: 'order_tracking_data',
    maxSizeGB: 2,
    strict: false,
    requiredColumns: [
      'order_no_',
      'customer_name', 
      'order_qty',
      'order_date',
      'order_stage'
    ],
    columns: [
      // Order Identification & Management
      'division', 'order_no_', 'pick_number', 'nsr', 'nsr_desc', 'sts',
      // Order Details
      'ord_type', 'ord_src', 'order_qty', 'order_date', 'backord', 'comments', 'hold_rsn',
      // Customer Information  
      'customer_name', 'customer', 'po_no',
      // Dates & Tracking
      'entry_date', 'estimate_cartons', 'order_stage', 'routing_note',
      // Organization
      'dept', 'ending', 'salesperson_1', 'start',
      // Product Details
      'style', 'color', 'season', 'season_name', 'style_name', 'style_season',
      // Packaging & Quantities
      'carton_qty', 'pack_qty', 'conf_type', 'bulk_no', 'manual_bulk',
      // Pricing Information
      'net_price', 'net_ext_price', 'det_ext_ret_price', 'dtl_ext_royalty', 'detail_cost', 'detail_ext_cost',
      // Discounts & Allowances
      'dtl_line_disc', 'dtl_ext_line_disc', 'dtl_net_ext_alw', 'dtl_net_alw',
      // Profitability Metrics
      'ext_gp_sss__std_cost_', 'gp__', 'gp_sss____std_cost_', 'gross_profit',
      // Location & Logistics
      'loc', 'program', 'category',
      // Labeling & Documentation
      'label_code', 'label', 'label_name',
      // Reporting & Invoice
      'mabd', 'rpt_date', 'invoice_date', 'total_amt_inv',
      // Additional Classifications
      'use_fct', 'type', 'type_name'
    ]
  }
};

// 🆕 ROUTING APP SCHEMAS (for tenant uploads)
const ROUTING_SCHEMAS = {
  routing_erp_orders: {
    name: 'ERP Orders',
    icon: '📋',
    tableName: 'routing_erp_orders',
    maxSizeGB: 2,
    strict: false,
    description: 'ERP order data for routing - POs, pick tickets, amounts',
    columns: [],
    requiredColumns: ['po_number', 'pick_number']
  },
  routing_supplier_one: {
    name: 'Supplier One Orders',
    icon: '✳',
    tableName: 'routing_supplier_one',
    maxSizeGB: 2,
    strict: false,
    description: 'Walmart Supplier One portal data',
    columns: [],
    requiredColumns: ['po_number']
  },
  routing_warehouse_logs: {
    name: 'Warehouse Ship Logs',
    icon: '📦',
    tableName: 'routing_warehouse_logs',
    maxSizeGB: 1,
    strict: false,
    description: 'Warehouse ship logs and pick status data',
    columns: [],
    requiredColumns: ['pick_ticket']
  },
  routing_walmart_tms: {
    name: 'Walmart TMS',
    icon: '🚛',
    tableName: 'routing_walmart_tms',
    maxSizeGB: 2,
    strict: false,
    description: 'Walmart TMS routing data with load and carrier info',
    columns: [],
    requiredColumns: []  // Flexible - accept any Walmart TMS export
  },
  routing_store_data: {
    name: 'Store/DC Data',
    icon: '🏢',
    tableName: 'routing_store_data',
    maxSizeGB: 1,
    strict: false,
    description: 'Walmart/Sam\'s DC and store location data',
    columns: [],
    requiredColumns: ['location_id', 'location_type']
  },
  routing_transit_time: {
    name: 'Transit Time',
    icon: '⏱',
    tableName: 'routing_transit_time',
    maxSizeGB: 1,
    strict: false,
    description: 'Transit time from origin warehouse to destination',
    columns: [],
    requiredColumns: ['origin_code', 'destination_id', 'mode', 'transit_days']
  }
};

// Combine all schemas into one lookup

// CHA-465 — pick the worksheet to validate. Everything historically used SheetNames[0], which is
// correct for every feed whose file has one data sheet. Target's item export does not: its first
// sheet is `Guide`, and the item sheet sits third. Validating the Guide sheet produced
// data-quality ERRORS (isValid is errors.length === 0), so a perfectly valid export was blocked
// before it ever reached storage.
//
// Only schemas that opt in via `sheetSelect: 'skipReference'` change behaviour; every other feed
// still gets SheetNames[0] exactly as before.
const REFERENCE_SHEETS = new Set(['Guide', 'DropdownData', 'Multi-values Reference']);
function pickValidationSheet(workbook, schema) {
    const names = workbook.SheetNames || [];
    if (schema && schema.sheetSelect === 'skipReference') {
        const found = names.find(n => !REFERENCE_SHEETS.has(String(n).trim()));
        if (found) return found;
    }
    return names[0];
}

export const ALL_FILE_SCHEMAS = {
  ...INMOCEAN_FILE_SCHEMAS,
  ...INMOCEAN_ORDER_SCHEMAS,
  ...ROUTING_SCHEMAS
};

class InMoceanValidationServiceClass {
    constructor() {
        // 🚀 NEW: Smart validation thresholds
        this.FAST_TRACK_SIZE_MB = 10; // Files over 10MB get fast-track validation
        this.SAMPLE_SIZE_FOR_LARGE_FILES = 1000; // Only validate first 1000 rows for large files
        
        console.log('🔧 InMoceanValidationService initialized (v9 - Smart Large File Support + Order Files)');
    }

    normalizeColumnName(columnName) {
        if (!columnName) return '';
        return columnName.toString().trim().replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').toLowerCase();
    }

    /**
     * 🚀 ENHANCED: Smart validation - fast for large files, comprehensive for small files
     * NOW SUPPORTS BOTH COSTING AND ORDER FILES
     */
    async validateFile(file, fileType, onProgress = null) {
        const validationResult = {
            isValid: false, errors: [], warnings: [], info: {}, parsedData: null, headers: []
        };

        try {
            // 🆕 ROUTING FILES: Skip validation entirely - just pass through
            if (fileType.startsWith('routing_')) {
                console.log(`🚛 Routing file detected (${fileType}) - skipping validation`);
                validationResult.isValid = true;
                validationResult.info = {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: fileType,
                    message: 'Routing files bypass validation'
                };
                return validationResult;
            }

            // 🆕 UPDATED: Check both costing and order schemas
            const schema = ALL_FILE_SCHEMAS[fileType];
            if (!schema) {
                throw new Error(`Unknown file type: ${fileType}. Supported types: ${Object.keys(ALL_FILE_SCHEMAS).join(', ')}`);
            }

            // 🐛 DEBUG: Log the schema being used
            console.log(`🔍 DEBUG: Using schema for ${fileType}:`, {
                name: schema.name,
                columns: schema.columns?.slice(0, 5), // First 5 columns
                requiredColumns: schema.requiredColumns,
                strict: schema.strict,
                isOrderFile: fileType in INMOCEAN_ORDER_SCHEMAS
            });

            const fileSizeMB = file.size / (1024 * 1024);
            const isLargeFile = fileSizeMB > this.FAST_TRACK_SIZE_MB;

            if (onProgress) onProgress('Checking file format and size...');
            this.validateBasicFile(file, schema, validationResult);

            if (isLargeFile) {
                // 🚀 FAST-TRACK: Large files get minimal browser validation
                console.log(`⚡ Large file detected (${fileSizeMB.toFixed(1)}MB) - using fast-track validation`);
                await this.fastTrackValidation(file, schema, validationResult, onProgress);
            } else {
                // 📊 FULL: Small files get comprehensive validation
                console.log(`📊 Small file (${fileSizeMB.toFixed(1)}MB) - using full validation`);
                await this.fullValidation(file, schema, validationResult, onProgress);
            }

            validationResult.isValid = validationResult.errors.length === 0;
            const statusMessage = validationResult.isValid ? 'Validation passed!' : 'Validation failed!';
            if (onProgress) onProgress(statusMessage);
            
            return validationResult;

        } catch (error) {
            validationResult.errors.push(error.message);
            validationResult.isValid = false;
            return validationResult;
        }
    }

    /**
     * 🚀 FAST-TRACK: Minimal validation for large files
     */
    async fastTrackValidation(file, schema, result, onProgress) {
        if (onProgress) onProgress('Fast-track validation for large file...');
        
        // Only validate headers and first few rows
        const sampleData = await this.parseFileHeadersAndSample(file, this.SAMPLE_SIZE_FOR_LARGE_FILES, schema);
        
        // Ensure headers is an array
        if (!sampleData.headers || !Array.isArray(sampleData.headers)) {
            result.errors.push('Failed to parse file headers. Please check the file format.');
            result.isValid = false;
            return;
        }
        
        // Ensure data is an array
        if (!sampleData.data || !Array.isArray(sampleData.data)) {
            result.errors.push('Failed to parse file data. Please check the file format.');
            result.isValid = false;
            return;
        }
        
        result.headers = sampleData.headers;
        result.parsedData = null; // Don't store full data for large files
        result.info.totalRows = sampleData.estimatedTotalRows;
        result.info.actualColumns = sampleData.headers.length;
        result.info.validationMode = 'fast-track';
        result.info.sampleSize = sampleData.data.length;

        if (onProgress) onProgress('Validating file structure (headers only)...');
        this.validateFileStructure(sampleData.headers, schema, result);

        if (result.errors.length === 0) {
            if (onProgress) onProgress('Quick data quality check...');
            this.validateDataQuality(sampleData.data, sampleData.headers, result);
            
            // Add fast-track specific warnings
            result.warnings.push(`Large file: Only first ${this.SAMPLE_SIZE_FOR_LARGE_FILES} rows validated. Full validation will happen server-side.`);
        }
    }

    /**
     * 📊 FULL: Comprehensive validation for small files (your existing logic)
     */
    async fullValidation(file, schema, result, onProgress) {
        if (onProgress) onProgress('Parsing file content...');
        const parsedResult = await this.parseFileContent(file, schema);
        const { data, headers } = parsedResult;
        
        // Ensure headers is an array
        if (!headers || !Array.isArray(headers)) {
            result.errors.push('Failed to parse file headers. Please check the file format.');
            result.isValid = false;
            return;
        }
        
        // Ensure data is an array
        if (!data || !Array.isArray(data)) {
            result.errors.push('Failed to parse file data. Please check the file format.');
            result.isValid = false;
            return;
        }
        
        result.parsedData = data;
        result.headers = headers;
        result.info.totalRows = data.length;
        result.info.actualColumns = headers.length;
        result.info.validationMode = 'comprehensive';

        if (onProgress) onProgress('Validating file structure...');
        this.validateFileStructure(headers, schema, result);
        
        if (result.errors.length === 0) {
            if (onProgress) onProgress('Checking data quality...');
            this.validateDataQuality(data, headers, result);
        }
    }

    /**
     * 🚀 NEW: Parse only headers and sample rows for large files
     */
    async parseFileHeadersAndSample(file, maxRows = 1000, schema = null) {
        return new Promise((resolve, reject) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    let allRows = [];
                    
                    if (fileExtension === 'csv') {
                        // Parse CSV file
                        const text = e.target.result;
                        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                        
                        // Simple CSV parsing (handles quoted fields)
                        allRows = lines.slice(0, maxRows + 10).map(line => {
                            const row = [];
                            let current = '';
                            let inQuotes = false;
                            
                            for (let i = 0; i < line.length; i++) {
                                const char = line[i];
                                if (char === '"') {
                                    inQuotes = !inQuotes;
                                } else if (char === ',' && !inQuotes) {
                                    row.push(current.trim());
                                    current = '';
                                } else {
                                    current += char;
                                }
                            }
                            row.push(current.trim());
                            return row;
                        });
                    } else {
                        // Parse Excel file
                        const workbook = XLSX.read(e.target.result, { 
                            type: 'binary',
                            sheetRows: maxRows + 10 // Only read first N rows + header detection
                        });
                        
                        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                            return reject(new Error("Excel file is empty or corrupted."));
                        }

                        const worksheet = workbook.Sheets[pickValidationSheet(workbook, schema)];
                        allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
                    }
                    
                    if (allRows.length === 0) return resolve({ headers: [], data: [], estimatedTotalRows: 0 });

                    // Find header row (same logic as before)
                    let bestGuessHeaderIndex = -1;
                    let maxColumns = 0;
                    for (let i = 0; i < Math.min(allRows.length, 10); i++) {
                        const row = allRows[i] || [];
                        const columnCount = row.filter(cell => cell !== null && String(cell).trim() !== '').length;
                        if (columnCount > maxColumns) {
                            maxColumns = columnCount;
                            bestGuessHeaderIndex = i;
                        }
                    }

                    if (bestGuessHeaderIndex === -1) {
                        return reject(new Error('Could not automatically detect a valid header row.'));
                    }

                    // Ensure header row exists and is an array
                    const headerRow = allRows[bestGuessHeaderIndex];
                    if (!headerRow || !Array.isArray(headerRow)) {
                        return reject(new Error('Invalid header row detected.'));
                    }
                    
                    const headers = headerRow.map(h => String(h || '').trim()).filter(h => h !== '');
                    if (headers.length === 0) {
                        return reject(new Error('No valid headers found in the file.'));
                    }
                    
                    const dataRows = allRows.slice(bestGuessHeaderIndex + 1, bestGuessHeaderIndex + 1 + maxRows);
                    
                    // Convert to objects for validation
                    const jsonData = dataRows.map(row => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] || null;
                        });
                        return obj;
                    });

                    const cleanedData = jsonData.filter(row => 
                        Object.values(row).some(val => val !== null && String(val).trim() !== '')
                    );

                    // Estimate total rows (rough calculation)
                    const fileSizeMB = file.size / (1024 * 1024);
                    const avgBytesPerRow = file.size / Math.max(allRows.length, 1);
                    const estimatedTotalRows = Math.floor(file.size / avgBytesPerRow) - 1; // -1 for header

                    resolve({ 
                        headers, 
                        data: cleanedData, 
                        estimatedTotalRows: Math.max(estimatedTotalRows, cleanedData.length)
                    });

                } catch (error) {
                    reject(new Error(`File parsing failed: ${error.message}`));
                }
            };
            
            reader.onerror = () => reject(new Error('Failed to read the file.'));
            
            // Use readAsText for CSV, readAsBinaryString for Excel
            if (fileExtension === 'csv') {
                reader.readAsText(file);
            } else {
                reader.readAsBinaryString(file);
            }
        });
    }

    // 📊 EXISTING METHODS (keeping your exact logic)
    validateBasicFile(file, schema, result) {
        const maxSizeBytes = schema.maxSizeGB * 1024 * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            result.errors.push(`File size (${this.formatFileSize(file.size)}) exceeds limit of ${schema.maxSizeGB}GB`);
        }
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
            result.errors.push(`Unsupported file format: .${fileExtension}.`);
        }
        result.info.fileSize = this.formatFileSize(file.size);
        result.info.fileFormat = fileExtension.toUpperCase();
    }

    async parseFileContent(file, schema = null) {
        return new Promise((resolve, reject) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    let allRows = [];
                    
                    if (fileExtension === 'csv') {
                        // Parse CSV file
                        const text = e.target.result;
                        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                        
                        // Simple CSV parsing (handles quoted fields)
                        allRows = lines.map(line => {
                            const row = [];
                            let current = '';
                            let inQuotes = false;
                            
                            for (let i = 0; i < line.length; i++) {
                                const char = line[i];
                                if (char === '"') {
                                    inQuotes = !inQuotes;
                                } else if (char === ',' && !inQuotes) {
                                    row.push(current.trim());
                                    current = '';
                                } else {
                                    current += char;
                                }
                            }
                            row.push(current.trim());
                            return row;
                        });
                    } else {
                        // Parse Excel file
                        const workbook = XLSX.read(e.target.result, { type: 'binary' });
                        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                            return reject(new Error("Excel file is empty or corrupted."));
                        }
                        const worksheet = workbook.Sheets[pickValidationSheet(workbook, schema)];
                        allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
                    }
                    
                    if (allRows.length === 0) return resolve({ headers: [], data: [] });

                    let bestGuessHeaderIndex = -1;
                    let maxColumns = 0;
                    for (let i = 0; i < Math.min(allRows.length, 10); i++) {
                        const row = allRows[i] || [];
                        const columnCount = row.filter(cell => cell !== null && String(cell).trim() !== '').length;
                        if (columnCount > maxColumns) {
                            maxColumns = columnCount;
                            bestGuessHeaderIndex = i;
                        }
                    }

                    if (bestGuessHeaderIndex === -1) {
                        return reject(new Error('Could not automatically detect a valid header row.'));
                    }
                    
                    // Ensure header row exists and is an array
                    const headerRow = allRows[bestGuessHeaderIndex];
                    if (!headerRow || !Array.isArray(headerRow)) {
                        return reject(new Error('Invalid header row detected.'));
                    }
                    
                    const headers = headerRow.map(h => String(h || '').trim()).filter(h => h !== '');
                    if (headers.length === 0) {
                        return reject(new Error('No valid headers found in the file.'));
                    }
                    
                    // Convert data rows to objects
                    const dataRows = allRows.slice(bestGuessHeaderIndex + 1);
                    const jsonData = dataRows.map(row => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] || null;
                        });
                        return obj;
                    });
                    
                    const cleanedData = jsonData.filter(row => Object.values(row).some(val => val !== null && String(val).trim() !== ''));
                    resolve({ headers, data: cleanedData });
                } catch (error) {
                    reject(new Error(`File parsing failed: ${error.message}`));
                }
            };
            
            reader.onerror = () => reject(new Error('Failed to read the file.'));
            
            // Use readAsText for CSV, readAsBinaryString for Excel
            if (fileExtension === 'csv') {
                reader.readAsText(file);
            } else {
                reader.readAsBinaryString(file);
            }
        });
    }

    validateFileStructure(actualHeaders, schema, result) {
        // Your existing validation logic (unchanged)
        if (!schema || !actualHeaders) return;

        const normalizedActual = actualHeaders.map(this.normalizeColumnName);
        const requiredColumns = schema.requiredColumns || [];

        if (requiredColumns.length > 0) {
            // `_extra_col_N` entries are backend positional PLACEHOLDERS (bigqueryProcessor pads them when the
            // ERP adds columns) — never real headers in the file, so don't treat them as "missing" and block
            // the upload (openwip failed when the ERP added 2 columns; the loader already handles the extras).
            const missing = requiredColumns.filter(col => !/^_extra_col/i.test(String(col).trim()) && !normalizedActual.includes(this.normalizeColumnName(col)));
            if (missing.length > 0) {
                result.errors.push(`Missing required columns: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? ` and ${missing.length - 5} more` : ''}`);
            }
        }

        if (schema.strict && Array.isArray(schema.columns) && schema.columns.length > 0) {
            const normalizedExpected = schema.columns.map(this.normalizeColumnName);
            const extra = actualHeaders.filter(col => col && col.trim() && !normalizedExpected.includes(this.normalizeColumnName(col)));
            if (extra.length > 0) {
                result.warnings.push(`Unexpected columns found (will be ignored): ${extra.slice(0, 3).join(', ')}`);
            }
        }
    }

    validateDataQuality(data, headers, result) {
        // Your existing data quality logic (unchanged)
        const sampleSize = Math.min(data.length, 1000);
        if (sampleSize === 0) return;

        // Ensure headers is an array
        if (!headers || !Array.isArray(headers)) {
            console.warn('⚠️ Headers is not an array:', headers);
            return;
        }

        const columnEmptiness = {};
        headers.forEach(h => { columnEmptiness[h] = 0; });

        for (let i = 0; i < sampleSize; i++) {
            const row = data[i];
            headers.forEach(h => {
                if (row[h] === null || String(row[h]).trim() === '') {
                    columnEmptiness[h]++;
                }
            });
        }
        
        const highEmptinessColumns = Object.entries(columnEmptiness)
            .filter(([, count]) => count / sampleSize > 0.95)
            .map(([col]) => col);
        
        if (highEmptinessColumns.length > 0) {
            result.warnings.push(`Columns with high emptiness: ${highEmptinessColumns.slice(0, 3).join(', ')}`);
        }
    }

    formatFileSize(bytes) {
        // Your existing formatFileSize method (unchanged)
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}

export const InMoceanValidationService = new InMoceanValidationServiceClass();
